/**
 * @fileoverview Utilities Module - Provides reusable helper functions for the application.
 * @module AppUtils
 */

/**
 * Namespace for application utility functions.
 * @namespace AppUtils
 */
const AppUtils = {
  /**
   * Maps a slider position (0-100) to a value using non-linear (exponential) scaling.
   * Useful for price sliders where sensitivity is needed at lower ranges.
   * @param {number} position - The slider position (0-100).
   * @param {number} min - Minimum output value
   * @param {number} max - Maximum output value
   * @param {number} exponent - Power function exponent (higher = more weight to lower values)
   * @param {number} roundTo - The value to round the result to (e.g., 10, 100, 5000).
   * @returns {number} The calculated value, rounded to the nearest multiple of `roundTo`.
   */
  sliderToValue: function(position, min, max, exponent, roundTo) {
    // Normalize position to 0-1 range
    const normalizedPosition = position / 100;
    
    // Apply power function scaling
    const scaledPosition = Math.pow(normalizedPosition, exponent);
    
    // Map to price range
    const value = min + scaledPosition * (max - min);
    
    // Round appropriately
    return Math.round(value / roundTo) * roundTo;
  },

  /**
   * Maps a value back to a non-linear slider position (0-100).
   * This is the inverse operation of `sliderToValue`.
   * @param {number} value - The actual value to map back to a slider position.
   * @param {number} min - The minimum value of the range.
   * @param {number} max - The maximum value of the range.
   * @param {number} exponent - The exponent used in the original `sliderToValue` scaling.
   * @returns {number} The corresponding slider position (0-100).
   */
  valueToSlider: function(value, min, max, exponent) {
    // Handle edge case where value might be outside the range slightly due to rounding
    if (value <= min) return 0;
    if (value >= max) return 100;

    // Normalize value within the 0-1 range
    const normalizedValue = (value - min) / (max - min);
    
    // Apply inverse of power function
    const position = Math.pow(normalizedValue, 1 / exponent) * 100;
    
    return position;
  },

  /**
   * Gets a standardized price in USD from a listing object.
   * Prioritizes `price_usd`, then converts `price_naf` using the rate from `AppConfig`,
   * then falls back to `price_filter_usd`.
   * @param {object} listing - The property listing data object.
   * @returns {number|NaN} The calculated price in USD, or NaN if no valid price can be determined.
   */
  getListingPriceUSD: function(listing) {
    // Try USD price first
    let priceUSD = parseInt(listing.price_usd); // Use parseInt for whole numbers

    // If not available, try /XCG conversion
    if (isNaN(priceUSD)) {
      const priceNAF = parseInt(listing.price_naf); // Use parseInt
      if (!isNaN(priceNAF)) {
        // Ensure AppConfig and currency rate are available
        if (typeof AppConfig !== 'undefined' && AppConfig.currency && AppConfig.currency.nafToUsd) {
          priceUSD = Math.round(priceNAF * AppConfig.currency.nafToUsd);
        } else {
          console.warn("[AppUtils] AppConfig.currency.nafToUsd not available for conversion.");
          // Fallback or leave as NaN? Decide based on requirements. Currently leaves as NaN.
        }
      }
      // If still not available, try filter price (assuming this is already USD)
      else if (listing.price_filter_usd) {
        priceUSD = parseInt(listing.price_filter_usd); // Use parseInt
      }
    }

    return priceUSD; // Will return NaN if no price was found/calculated
  },

  /**
   * Formats a number with dots as thousand separators.
   * @param {number|string|null|undefined} x - The number or string to format.
   * @returns {string} Formatted number string with dot separators (e.g., "1.234.567").
   */
  numberWithCommas: function(x) { // Function name is a bit misleading now
    // Convert to string and handle potential null/undefined
    const numStr = String(x || '');
    // Use dots as thousand separators (as per original code logic)
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  },

  /**
   * Capitalizes the first letter of a string.
   * @param {string} str - The string to capitalize.
   * @returns {string} The string with its first letter capitalized, or an empty string if input is falsy.
   */
  capitalize: function(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1); // Only capitalizes the very first letter
  },

  /**
   * Sends the Google Analytics 'listing_click' event when a visitor follows a listing to the realtor.
   * CONTEXT: Called from the inline onclick of every "to the realtor" link on the map (popup realtor
   *   name, "View details" button, image "Source" link) and in the list view (thumbnail, "View details").
   * UPSTREAM: onclick="AppUtils.trackListingClick('<realtor_id>')" in map-module.js and list-view-module.js.
   * DOWNSTREAM: window.gtag() from cookie-consent.js, which queues the event and sends it only when
   *   the visitor allows analytics (see that file).
   * FAILURE: gtag() is missing when cookie-consent.js didn't run (content blockers stop that file name).
   *   Then the click is simply not tracked: the link still opens and no error is thrown (it used to
   *   throw "gtag is not defined", Raygun 3rjbch5o).
   * @param {string} realtorId - The listing's realtor_id ('' when unknown).
   */
  trackListingClick: function(realtorId) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'listing_click', { 'realtor_id': realtorId });
  },

  /**
   * Generates a placeholder SVG image with translated "image unavailable" text.
   * Used when property images fail to load or are missing.
   * @returns {string} Base64 encoded data URI for the SVG placeholder image.
   */
  getPlaceholderImageSvg: function() {
    const text = t('property.imageUnavailable');
    const svg = `<svg width="320" height="160" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f1f3f4"/><path d="M160,75 L220,120 L100,120 Z" fill="#d2d6dc"/><circle cx="190" cy="60" r="15" fill="#d2d6dc"/><text x="160" y="90" font-family="Arial" font-size="12" text-anchor="middle" fill="#444">${text}</text></svg>`;
    return 'data:image/svg+xml;base64,' + btoa(svg);
  },

  /**
   * Checks whether a newer data build is live and, if so, reloads the page.
   * CONTEXT: Keeps long-open map tabs fresh: compares AppConfig.cachebust with /map-assets/data/latest.txt.
   * UPSTREAM: Called from the 'visibilitychange' listener in templates/index.html whenever the tab becomes visible again.
   * DOWNSTREAM: window.location.reload() when the data version differs; otherwise nothing happens.
   * FAILURE: A failed fetch (common on phones waking from the background, before the network is back) is only
   *   logged with console.warn. The check simply runs again the next time the tab becomes visible.
   */
  checkForNewData: function() {
    //fetch the check if value in AppConfig.dataVersion is different from the contents of the /map-assets/data/latest.txt
    fetch('/map-assets/data/latest.txt'+'?rnd=' + Math.random().toString(36).substring(2, 12)+Math.random().toString(36).substring(2, 12))
      .then(response => response.text())
      .then(latestcachebust => {
        // Compare with the current version in AppConfig
        if (latestcachebust.trim() !== AppConfig.cachebust) {
          console.log('New data version detected, reloading property data');
          window.location.reload();
        }
        else {
          //console.log('No new data version detected');
        }
      })
      .catch(error => {
        // Plain console.warn on purpose: `Logger` only exists inside MapModule/GeoboundsSelectorModule,
        // so calling it here threw "Logger is not defined" (reported to Raygun) instead of logging.
        console.warn('Error checking for new data:', error);
      });
  }
};

/**
 * Exposes the AppUtils object globally on the window object
 * for easy access by other modules.
 */
window.AppUtils = AppUtils;
